// Zoho Embedded App SDK: https://live.zwidgets.com/js-sdk/1.1/ZohoEmbededAppSDK.min.js

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";

import { getLogRecords, FIELDS } from "../../../APICalling/logApi";
import { getAllSetting, upsertSetting } from "../../../APICalling/settingApi";
import { getShopifyWebhooks } from "../../../APICalling/shopifyApi";
import { preUninstallShopify } from "../../../APICalling/shopifyAdminApi";
import {
  getOrgVariable,
  setUpOrgVariable,
  setZohoWorkflowsStatus,
  revokeZoho,
} from "../../../APICalling/zohoAdminApi";
import {
  EXTENSION_IDENTIFIER,
  LOG_ERRORS_API_NAME,
  SHOPIFY_ORG_CONN,
  ZOHO_CONN,
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
} from "../../../config/serviceConstant";
import {
  getOpenSnakSuccessData,
  getOpenSnakErrorData,
} from "../../../config/otherConstant";
import { FONT_HEADING } from "../../../theme";

const ZOHO = window.ZOHO;

// Demo Uninstall button visibility. The pre-uninstall cleanup flow
// (runDemoUninstall + its confirmation dialog) is fully wired and kept for
// later use — this flag only hides the entry-point button from merchants.
const SHOW_DEMO_UNINSTALL = false;

// Needs Attention panel visibility. Hidden for now — its "Test Sync" item is
// not implemented and the review/failed CTAs aren't wired to navigation yet.
// The panel code (attention items, AttentionRow) is kept intact; flip to true
// to bring it back. While hidden, Recent Sync Activity spans the full width.
const SHOW_NEEDS_ATTENTION = false;

// ── Helpers ──

const fmtDateTime = (iso) => {
  if (!iso) return { date: "—", time: "" };
  try {
    const d = new Date(iso);
    if (isNaN(d)) return { date: "—", time: "" };
    return {
      date: d.toLocaleDateString([], { month: "short", day: "numeric" }),
      time: d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  } catch {
    return { date: "—", time: "" };
  }
};

const shopFromUrl = (url) => {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/\.myshopify\.com$/, "");
  } catch {
    return "";
  }
};

const parseSchema = (s) => {
  let schema = s?.settingSchema || s?.setting_schema || {};
  if (typeof schema === "string") {
    try {
      schema = JSON.parse(schema);
    } catch {
      schema = {};
    }
  }
  return schema || {};
};

// Friendly "Customer Created" / "Order Updated" label from the log Name
// (which usually looks like "customers/create - 123456789")
const prettifyEvent = (name) => {
  if (!name || typeof name !== "string") return "—";
  const head = name.split(" - ")[0] || name;
  const [mod, action] = head.split("/");
  if (!mod || !action) return name;
  const singular = mod.replace(/s$/, ""); // customers -> customer
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const actionMap = {
    create: "Created",
    update: "Updated",
    updated: "Updated",
    delete: "Deleted",
    paid: "Paid",
    fulfilled: "Fulfilled",
    cancelled: "Cancelled",
  };
  return `${cap(singular)} ${actionMap[action] || cap(action)}`;
};

// ── Stat-card config (display only; values are filled from state) ──

const STAT_CONFIGS = [
  {
    key: "stores",
    icon: StorefrontOutlinedIcon,
    iconBg: "#ecfdf5",
    iconColor: "#047857",
    label: "Connected Stores",
    subColor: "#16a34a",
    subDot: true,
  },
  {
    key: "mappings",
    icon: LinkOutlinedIcon,
    iconBg: "#eff6ff",
    iconColor: "#2563eb",
    label: "Active Mappings",
    subColor: "#2563eb",
    subDot: true,
  },
  {
    key: "webhooks",
    icon: HubOutlinedIcon,
    iconBg: "#f5f3ff",
    iconColor: "#7c3aed",
    label: "Active Webhooks",
    subColor: "#16a34a",
    subDot: true,
  },
  {
    key: "success",
    icon: CheckCircleOutlineIcon,
    iconBg: "#ecfdf5",
    iconColor: "#047857",
    label: "Successful Syncs",
    subColor: "#71717a",
  },
  {
    key: "failed",
    icon: HighlightOffOutlinedIcon,
    iconBg: "#fef2f2",
    iconColor: "#dc2626",
    label: "Failed Syncs",
    subColor: "#dc2626",
  },
];

// ── Subcomponents ──

function StatCard({ config, value, sub, loading }) {
  const Icon = config.icon;
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e4e4e7",
        borderRadius: "12px",
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: config.iconBg,
          color: config.iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 22,
            fontWeight: 700,
            color: "#09090b",
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          {loading ? (
            <CircularProgress size={14} sx={{ color: "#a1a1aa" }} />
          ) : (
            value
          )}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#52525b", fontWeight: 500 }}>
          {config.label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: "2px" }}>
          {config.subDot && (
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: config.subColor,
              }}
            />
          )}
          <Typography
            sx={{ fontSize: 12, fontWeight: 500, color: config.subColor }}
          >
            {sub}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ActivityStatusPill({ status }) {
  const lower = (status || "").toLowerCase();
  const ok = lower === "success";
  const styles = ok
    ? { bg: "#dcfce7", fg: "#166534", label: "Success" }
    : lower === "failed"
    ? { bg: "#fee2e2", fg: "#991b1b", label: "Failed" }
    : { bg: "#fef3c7", fg: "#92400e", label: status || "—" };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 12,
        fontWeight: 600,
        px: "8px",
        py: "1px",
        borderRadius: "9999px",
        bgcolor: styles.bg,
        color: styles.fg,
      }}
    >
      {styles.label}
    </Box>
  );
}

function AttentionRow({ item }) {
  const Icon = item.icon;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        py: 1.5,
        "&:not(:last-of-type)": { borderBottom: "1px solid #f4f4f5" },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          bgcolor: item.iconBg,
          color: item.iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: "2px",
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 14,
            fontWeight: 700,
            color: "#09090b",
            mb: "2px",
          }}
        >
          {item.title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#71717a", lineHeight: 1.45 }}>
          {item.desc}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        onClick={item.onClick}
        sx={{
          textTransform: "none",
          fontSize: 12,
          fontWeight: 600,
          flexShrink: 0,
          color: item.ctaColor,
          borderColor: item.ctaBorder,
          "&:hover": { bgcolor: item.ctaHoverBg, borderColor: item.ctaColor },
        }}
      >
        {item.cta}
      </Button>
    </Box>
  );
}

// ── Main ──

export default function Dashboard({
  orgId,
  apiKey,
  dataCenterUrl,
  dataCenter,
  shopifyStores,
  setSnack,
}) {
  const [loading, setLoading] = useState(true);
  const [mappingCounts, setMappingCounts] = useState({ active: 0, needReview: 0 });
  const [webhooksCount, setWebhooksCount] = useState(0);
  const [successToday, setSuccessToday] = useState(0);
  const [failedToday, setFailedToday] = useState(0);
  const [activity, setActivity] = useState([]);
  const [totalActivity, setTotalActivity] = useState(0);

  // ── Two-way sync toggle ──
  // State is persisted in the __Log_Errors org variable. When ON, the three
  // Zoho → Shopify outbound workflows are active so CRM edits flow back to
  // Shopify; when OFF only the one-way Shopify → Zoho webhooks run.
  const [bothWaySync, setBothWaySync] = useState(true);
  const [bothWaySaving, setBothWaySaving] = useState(false);
  // Confirmation dialog: holds the value the user is trying to switch to so we
  // only commit after they confirm in the popup.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingBothWay, setPendingBothWay] = useState(false);

  // ── Demo Uninstall ──
  // Runs the same pre-uninstall cleanup the Deluge uninstall hook performs:
  // Shopify-side cleanup (webhooks + tokens + settings) then revoke Zoho.
  const [uninstallOpen, setUninstallOpen] = useState(false);
  const [uninstalling, setUninstalling] = useState(false);

  const storesCount = (shopifyStores || []).length;

  // Build per-store headers for webhook calls
  const buildShopifyServiceHeaders = (shopDomain) => ({
    orgid: orgId,
    connname: EXTENSION_IDENTIFIER,
    apikey: apiKey,
    datacenterurl: dataCenterUrl,
    datacenter: dataCenter,
    [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(shopDomain),
  });

  // Headers the workflows-status endpoint expects (orgid/apikey/datacenterurl)
  const buildWorkflowHeaders = () => ({
    orgid: orgId,
    apikey: apiKey,
    datacenterurl: dataCenterUrl,
  });

  // ── Bulk-flip the CRM → Shopify sync rules with the two-way toggle ──
  // The toggle is the master switch for the outbound direction: besides the
  // three Zoho → Shopify workflows, it also flips the `enabled` flag on the
  // direction: "CRM2Shopify" sync rules so the Sync Rules tab reflects
  // reality and the backend only applies active mappings.
  // Only crm/record/create rules are seeded by default; update/delete rules
  // exist only if the client added them via Add Mapping / custom workflows.
  //   ON  → enable every non-delete CRM2Shopify rule (seeded creates + any
  //         custom update rules). Delete rules stay as they are — pushing CRM
  //         deletions to a live store is opted into per-rule.
  //   OFF → disable ALL CRM2Shopify rules (including delete).
  const setCrmToShopifyRulesEnabled = async (enabled) => {
    const settingAuth = { orgId, apiKey, dataCenterUrl, shop: "" };
    const all = await getAllSetting(settingAuth);
    const targets = (all || [])
      .map((s) => {
        let schema = s.settingSchema ?? s.setting_schema ?? {};
        if (typeof schema === "string") {
          try { schema = JSON.parse(schema); } catch { schema = {}; }
        }
        return { raw: s, schema };
      })
      .filter(({ schema }) => schema?.direction === "CRM2Shopify")
      .filter(
        ({ schema }) =>
          // When enabling, leave delete rules alone; when disabling, take all.
          !enabled || schema?.triggerApiName !== "crm/record/delete"
      )
      .filter(({ schema }) => schema?.enabled !== enabled);

    let failures = 0;
    await Promise.all(
      targets.map(({ raw, schema }) => {
        const id = raw.settingId ?? raw.setting_id ?? raw.id;
        if (!id) return null;
        return upsertSetting(settingAuth, {
          settingId: id,
          settingSchema: { ...schema, enabled },
          moduleApiName:
            raw.moduleApiName ?? raw.module_name ?? schema.module ?? "",
          settingType: "systemDefined",
        }).then((ok) => {
          if (!ok) failures += 1;
        });
      })
    );
    return { updated: targets.length - failures, failures };
  };

  // ── Load the persisted two-way-sync state from the org variable ──
  // Defaults to ON when the variable has never been set.
  useEffect(() => {
    if (!ZOHO) return;
    let cancelled = false;
    (async () => {
      const raw = await getOrgVariable(ZOHO, LOG_ERRORS_API_NAME);
      if (cancelled) return;
      if (raw === null || raw === undefined || raw === "") {
        setBothWaySync(true);
      } else {
        setBothWaySync(raw === true || raw === "true");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Request a toggle ──
  // Clicking the checkbox does NOT flip it immediately — it records the target
  // value and opens the confirmation dialog. The checkbox stays controlled by
  // bothWaySync, so it visually holds its current state until the user confirms.
  const requestBothWayToggle = (event) => {
    setPendingBothWay(event.target.checked);
    setConfirmOpen(true);
  };

  const cancelBothWay = () => {
    if (bothWaySaving) return;
    setConfirmOpen(false);
  };

  // ── Commit the toggle (after confirm) ──
  // Calls the workflows-status endpoint, persists the new value to the org
  // variable, then updates the checkbox. Leaves the checkbox unchanged on error.
  const confirmBothWay = async () => {
    const next = pendingBothWay;
    setConfirmOpen(false);
    setBothWaySaving(true);
    try {
      const result = await setZohoWorkflowsStatus(buildWorkflowHeaders(), next);
      if (result === null) throw new Error("workflows status update failed");
      // Keep the CRM → Shopify sync rules in lock-step with the workflows.
      const { failures } = await setCrmToShopifyRulesEnabled(next);
      await setUpOrgVariable(ZOHO, LOG_ERRORS_API_NAME, String(next));
      setBothWaySync(next);
      if (failures > 0) {
        setSnack(
          getOpenSnakErrorData(
            `Two-way sync ${next ? "enabled" : "disabled"}, but ${failures} CRM to Shopify rule(s) couldn't be updated — check the Sync Rules tab.`,
            8000
          )
        );
      } else {
        setSnack(
          getOpenSnakSuccessData(
            next ? "Two-way sync enabled." : "Two-way sync disabled."
          )
        );
      }
    } catch (e) {
      setSnack(
        getOpenSnakErrorData("Couldn't update two-way sync. Please try again.")
      );
    } finally {
      setBothWaySaving(false);
    }
  };

  // ── Demo Uninstall: run the pre-uninstall cleanup ──
  // Faithful port of the Deluge pre-uninstall hook. Each step is wrapped in
  // its own try/catch so one failure never blocks the next, and every result
  // is collected into a log that's printed to the console.
  //   1. Shopify-side cleanup (delete webhooks + revoke tokens + delete settings)
  //      via the org-level connection (`<ext>__shopify`).
  //   2. Revoke the Zoho connection (`<ext>__zoho`).
  const runDemoUninstall = async () => {
    setUninstalling(true);
    const cleanupLogs = [];

    // 1. Shopify-side cleanup
    try {
      const resp = await preUninstallShopify({
        orgid: orgId,
        apikey: apiKey,
        connname: SHOPIFY_ORG_CONN,
        datacenterurl: dataCenterUrl,
        datacenter: dataCenter,
        "Content-Type": "application/json",
      });
      cleanupLogs.push({ step: "shopify_pre_uninstall", resp });
    } catch (e) {
      cleanupLogs.push({ step: "shopify_pre_uninstall_error", error: String(e) });
    }

    // 2. Revoke Zoho
    try {
      const resp = await revokeZoho({
        orgid: orgId,
        apikey: apiKey,
        connname: ZOHO_CONN,
        datacenterurl: dataCenterUrl,
        datacenter: dataCenter,
        "Content-Type": "application/json",
      });
      cleanupLogs.push({ step: "revoke_zoho", resp });
    } catch (e) {
      cleanupLogs.push({ step: "revoke_zoho_error", error: String(e) });
    }

    const shopifyOk = cleanupLogs.find(
      (l) => l.step === "shopify_pre_uninstall"
    )?.resp?.ok;
    setUninstalling(false);
    setUninstallOpen(false);
    if (shopifyOk === false) {
      setSnack(
        getOpenSnakErrorData(
          "Demo uninstall ran with errors — check the console for details."
        )
      );
    } else {
      setSnack(
        getOpenSnakSuccessData(
          "Demo uninstall cleanup executed. Check the console for details."
        )
      );
    }
  };

  useEffect(() => {
    if (!orgId || !apiKey || !dataCenterUrl) return;

    let cancelled = false;
    const run = async () => {
      setLoading(true);

      // ── Mappings: settingType !== "relatedList", enabled flag in schema ──
      const settingsPromise = (async () => {
        try {
          const all = await getAllSetting({
            orgId,
            apiKey,
            dataCenterUrl,
            shop: "",
          });
          const mappings = (all || []).filter((s) => {
            const t = s?.settingType ?? s?.setting_type ?? s?.type;
            return t !== "relatedList";
          });
          let active = 0;
          let needReview = 0;
          mappings.forEach((s) => {
            const schema = parseSchema(s);
            if (schema.enabled === true) active += 1;
            else needReview += 1;
          });
          if (!cancelled) setMappingCounts({ active, needReview });
        } catch (e) {
          if (!cancelled) setMappingCounts({ active: 0, needReview: 0 });
        }
      })();

      // ── Webhooks: sum across every connected store, fetched in parallel ──
      const webhooksPromise = (async () => {
        try {
          const stores = (shopifyStores || []).filter((s) => s?.domain);
          if (stores.length === 0) {
            if (!cancelled) setWebhooksCount(0);
            return;
          }
          const counts = await Promise.all(
            stores.map(async (store) => {
              try {
                const list = await getShopifyWebhooks(
                  buildShopifyServiceHeaders(store.domain)
                );
                return Array.isArray(list) ? list.length : 0;
              } catch {
                return 0;
              }
            })
          );
          const total = counts.reduce((a, b) => a + b, 0);
          if (!cancelled) setWebhooksCount(total);
        } catch {
          if (!cancelled) setWebhooksCount(0);
        }
      })();

      // ── Recent activity + today's success/failed tally ─────────────────
      // Fetch a large page in one call and derive both the recent-activity
      // rows and today's success/failed counts client-side. Avoids two
      // separate CRM criteria searches that flaked when the datetime value
      // contained colons (`+00:00`) — Zoho would return 0 even with matching
      // records present.
      const startOfTodayMs = (() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      })();

      const activityPromise = (async () => {
        try {
          if (!ZOHO) return;
          const resp = await getLogRecords(ZOHO, { perPage: 200 });
          const records = resp?.records || [];

          // Tally Success / Failed for records created on/after local midnight
          let sToday = 0;
          let fToday = 0;
          for (const raw of records) {
            const created = raw?.Created_Time;
            const ts = created ? new Date(created).getTime() : NaN;
            if (!isFinite(ts) || ts < startOfTodayMs) continue;
            const status = (raw?.[FIELDS.STATUS] || "").toLowerCase();
            if (status === "success") sToday += 1;
            else if (status === "failed") fToday += 1;
          }

          const rows = records.slice(0, 5).map((raw) => {
            const name = raw?.Name || "";
            const shopifyUrl = raw?.[FIELDS.SHOPIFY_URL] || "";
            const dt = fmtDateTime(raw?.Created_Time);
            return {
              date: dt.date,
              time: dt.time,
              store: shopFromUrl(shopifyUrl) || "—",
              event: prettifyEvent(name),
              module: raw?.[FIELDS.MODULE] || "—",
              status: raw?.[FIELDS.STATUS] || "",
            };
          });

          if (!cancelled) {
            setSuccessToday(sToday);
            setFailedToday(fToday);
            setActivity(rows);
            setTotalActivity(resp?.info?.count || rows.length);
          }
        } catch {
          if (!cancelled) {
            setSuccessToday(0);
            setFailedToday(0);
            setActivity([]);
            setTotalActivity(0);
          }
        }
      })();

      await Promise.all([
        settingsPromise,
        webhooksPromise,
        activityPromise,
      ]);
      if (!cancelled) setLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, apiKey, dataCenterUrl, dataCenter, shopifyStores]);

  // ── Derived stat values ──
  const statValues = useMemo(
    () => ({
      stores: {
        value: storesCount,
        sub: storesCount > 0 ? "All active" : "None connected",
      },
      mappings: {
        value: mappingCounts.active,
        sub:
          mappingCounts.needReview > 0
            ? `${mappingCounts.needReview} need review`
            : "All up to date",
      },
      webhooks: {
        value: webhooksCount,
        sub: webhooksCount > 0 ? "All healthy" : "None active",
      },
      success: {
        value: successToday,
        sub: "Today",
      },
      failed: {
        value: failedToday,
        sub: "Today",
      },
    }),
    [
      storesCount,
      mappingCounts.active,
      mappingCounts.needReview,
      webhooksCount,
      successToday,
      failedToday,
    ]
  );

  // ── Attention items (computed from state) ──
  // TODO: persist test-sync completion flag (orgVarMap or setting) and read it here
  const testSyncDone = false;
  const attention = useMemo(() => {
    const items = [];
    if (failedToday > 0) {
      items.push({
        key: "failed",
        icon: HighlightOffOutlinedIcon,
        iconBg: "#fef2f2",
        iconColor: "#dc2626",
        title: `${failedToday} Failed Sync${failedToday === 1 ? "" : "s"}`,
        desc: "Some records failed to sync today. Check the logs for details.",
        cta: "View Failed Logs",
        ctaColor: "#dc2626",
        ctaBorder: "#fecaca",
        ctaHoverBg: "#fef2f2",
      });
    }
    if (mappingCounts.needReview > 0) {
      items.push({
        key: "review",
        icon: WarningAmberOutlinedIcon,
        iconBg: "#fffbeb",
        iconColor: "#d97706",
        title: `${mappingCounts.needReview} Mapping${mappingCounts.needReview === 1 ? "" : "s"} Need Review`,
        desc: "Some mappings are disabled or missing required fields.",
        cta: "Review Mappings",
        ctaColor: "#d97706",
        ctaBorder: "#fde68a",
        ctaHoverBg: "#fffbeb",
      });
    }
    if (!testSyncDone) {
      items.push({
        key: "test",
        icon: InfoOutlinedIcon,
        iconBg: "#eff6ff",
        iconColor: "#2563eb",
        title: "Test Sync Not Completed",
        desc: "Run a test sync to ensure everything is configured correctly.",
        cta: "Run Test Sync",
        ctaColor: "#2563eb",
        ctaBorder: "#bfdbfe",
        ctaHoverBg: "#eff6ff",
        onClick: () =>
          setSnack &&
          setSnack({
            duration: 2000,
            open: true,
            severity: "info",
            message: "Test sync not yet implemented.",
          }),
      });
    }
    return items;
  }, [failedToday, mappingCounts.needReview, testSyncDone, setSnack]);

  return (
    <Box>
      {/* Top action row — Demo Uninstall (runs the pre-uninstall cleanup).
          Hidden from the UI for now; the full flow (runDemoUninstall + confirm
          dialog below) is kept intact. Flip SHOW_DEMO_UNINSTALL to bring the
          button back. */}
      {SHOW_DEMO_UNINSTALL && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setUninstallOpen(true)}
            disabled={uninstalling}
            startIcon={
              uninstalling ? (
                <CircularProgress size={14} sx={{ color: "#dc2626" }} />
              ) : (
                <PowerSettingsNewOutlinedIcon sx={{ fontSize: 17 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              color: "#dc2626",
              borderColor: "#fecaca",
              "&:hover": { borderColor: "#dc2626", bgcolor: "#fef2f2" },
            }}
          >
            Demo Uninstall
          </Button>
        </Box>
      )}

      {/* Two-way sync toggle (full-width) — icon + text on the left, the
          status pill + checkbox on the right. Only the checkbox is clickable. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          bgcolor: "#fff",
          border: "1px solid #e4e4e7",
          borderRadius: "12px",
          px: 2.5,
          py: 1.5,
          mb: 2.5,
        }}
      >
        {/* Left: icon beside the label + description block (non-interactive) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: "#ecfdf5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SwapHorizOutlinedIcon sx={{ fontSize: 20, color: "#0d6e1f" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_HEADING,
                fontSize: 15,
                fontWeight: 700,
                color: "#09090b",
                lineHeight: 1.45,
              }}
            >
              Both Way Sync
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: "#71717a", lineHeight: 1.45 }}
            >
              When on, changes in Zoho CRM are pushed back to Shopify. Turn off
              to keep sync one-way (Shopify → Zoho only).
            </Typography>
          </Box>
        </Box>

        {/* Right: status pill + checkbox (the only clickable control) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {bothWaySaving ? (
            <CircularProgress size={16} sx={{ color: "#0d6e1f" }} />
          ) : (
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                px: 1,
                py: "3px",
                borderRadius: "9999px",
                bgcolor: bothWaySync ? "#dcfce7" : "#f4f4f5",
                color: bothWaySync ? "#166534" : "#71717a",
                whiteSpace: "nowrap",
              }}
            >
              {bothWaySync ? "Currently On" : "Currently Off"}
            </Box>
          )}
          <Checkbox
            checked={bothWaySync}
            onChange={requestBothWayToggle}
            disabled={bothWaySaving}
            size="small"
            inputProps={{ "aria-label": "Toggle two-way sync" }}
            sx={{
              p: 0,
              color: "#a1a1aa",
              "&.Mui-checked": { color: "#0d6e1f" },
            }}
          />
        </Box>
      </Box>

      {/* Stats row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(5, 1fr)",
          },
          gap: 2,
          mb: 2.5,
        }}
      >
        {STAT_CONFIGS.map((config) => (
          <StatCard
            key={config.key}
            config={config}
            value={statValues[config.key].value}
            sub={statValues[config.key].sub}
            loading={loading}
          />
        ))}
      </Box>

      {/* Two-column section (single column while Needs Attention is hidden) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: SHOW_NEEDS_ATTENTION
            ? { xs: "1fr", lg: "minmax(0, 1.4fr) minmax(0, 1fr)" }
            : "1fr",
          gap: 2.5,
        }}
      >
        {/* Recent Sync Activity */}
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e4e4e7",
            borderRadius: "12px",
            p: 2.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_HEADING,
                fontSize: 15,
                fontWeight: 700,
                color: "#09090b",
                letterSpacing: "-0.01em",
              }}
            >
              Recent Sync Activity
            </Typography>
            <Typography
              component="button"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#2563eb",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                p: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View All Logs
            </Typography>
          </Box>

          {/* Header row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "0.7fr 1.3fr 1.4fr 1fr 0.8fr",
              gap: 1,
              alignItems: "center",
              px: 1,
              py: "8px",
              fontSize: 12,
              fontWeight: 600,
              color: "#71717a",
              borderBottom: "1px solid #f4f4f5",
            }}
          >
            <Box>Date &amp; Time</Box>
            <Box>Store</Box>
            <Box>Event</Box>
            <Box>Module</Box>
            <Box>Status</Box>
          </Box>

          {/* Rows / empty state / loading */}
          {loading ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={20} sx={{ color: "#a1a1aa" }} />
            </Box>
          ) : activity.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, color: "#71717a" }}>
                No sync activity yet.
              </Typography>
            </Box>
          ) : (
            activity.map((row, i) => (
              <Box
                key={i}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "0.7fr 1.3fr 1.4fr 1fr 0.8fr",
                  gap: 1,
                  alignItems: "center",
                  px: 1,
                  py: "10px",
                  fontSize: 13,
                  borderBottom:
                    i === activity.length - 1 ? "none" : "1px solid #f4f4f5",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13, color: "#09090b", fontWeight: 500, lineHeight: 1.2 }}>
                    {row.date}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#71717a", lineHeight: 1.2 }}>
                    {row.time}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      bgcolor: "#ecfdf5",
                      color: "#047857",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {(row.store || "—")[0].toUpperCase()}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#09090b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.store}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: "#09090b" }}>
                  {row.event}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#52525b" }}>
                  {row.module}
                </Typography>
                <Box>
                  <ActivityStatusPill status={row.status} />
                </Box>
              </Box>
            ))
          )}

          {!loading && activity.length > 0 && (
            <Typography
              sx={{
                fontSize: 12,
                color: "#71717a",
                mt: 1.5,
                textAlign: "left",
              }}
            >
              Showing {activity.length} of {totalActivity || activity.length} activities
            </Typography>
          )}
        </Box>

        {/* Needs Attention — hidden behind SHOW_NEEDS_ATTENTION for now */}
        {SHOW_NEEDS_ATTENTION && (
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e4e4e7",
            borderRadius: "12px",
            p: 2.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_HEADING,
                fontSize: 15,
                fontWeight: 700,
                color: "#09090b",
                letterSpacing: "-0.01em",
              }}
            >
              Needs Attention
            </Typography>
            <Typography
              component="button"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#2563eb",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                p: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View All
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={20} sx={{ color: "#a1a1aa" }} />
            </Box>
          ) : attention.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 28, color: "#16a34a", mb: 0.5 }}
              />
              <Typography
                sx={{
                  fontFamily: FONT_HEADING,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#09090b",
                }}
              >
                You're all set
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#71717a" }}>
                No issues need attention right now.
              </Typography>
            </Box>
          ) : (
            attention.map((item) => <AttentionRow key={item.key} item={item} />)
          )}
        </Box>
        )}
      </Box>

      {/* ── Two-way sync confirmation popup ── */}
      <Dialog
        open={confirmOpen}
        onClose={cancelBothWay}
        PaperProps={{ sx: { borderRadius: "12px", maxWidth: 460 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            fontFamily: FONT_HEADING,
            fontSize: 17,
            fontWeight: 700,
            pb: 1,
          }}
        >
          {pendingBothWay ? (
            <SwapHorizOutlinedIcon sx={{ fontSize: 22, color: "#0d6e1f" }} />
          ) : (
            <WarningAmberOutlinedIcon sx={{ fontSize: 22, color: "#d97706" }} />
          )}
          {pendingBothWay ? "Enable two-way sync?" : "Turn off two-way sync?"}
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#3f3f46", mb: 1.5 }}>
            {pendingBothWay
              ? "Changes you make in Zoho CRM — to Contacts, Sales Orders, and Products — will start syncing back to your Shopify store automatically."
              : "Sync will become one-way only: Shopify → Zoho. Changes you make in Zoho CRM will no longer be sent to Shopify."}
          </Typography>

          {/* Instruction + warning callout */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              p: "10px 12px",
              borderRadius: "8px",
              bgcolor: pendingBothWay ? "#f0fdf4" : "#fffbeb",
              border: `1px solid ${pendingBothWay ? "#bbf7d0" : "#fde68a"}`,
            }}
          >
            <WarningAmberOutlinedIcon
              sx={{
                fontSize: 17,
                mt: "1px",
                flexShrink: 0,
                color: pendingBothWay ? "#15803d" : "#92400e",
              }}
            />
            <Typography
              sx={{
                fontSize: 13,
                lineHeight: 1.5,
                color: pendingBothWay ? "#15803d" : "#92400e",
              }}
            >
              {pendingBothWay ? (
                <>
                  This activates all three Zoho → Shopify workflows and enables
                  your CRM to Shopify create/update sync rules (delete rules
                  stay off — enable them per rule from the Sync Rules tab).
                  Editing a synced record in CRM will update the matching
                  record in your live Shopify store, so make sure your field
                  mappings are correct first.
                </>
              ) : (
                <>
                  This disables all three Zoho → Shopify workflows and every
                  CRM to Shopify sync rule. Your Shopify store will stop
                  receiving updates from CRM until you switch this back on.
                  Existing records in both systems are left unchanged.
                </>
              )}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
          <Button
            onClick={cancelBothWay}
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              color: "#3f3f46",
              borderColor: "#e4e4e7",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmBothWay}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: pendingBothWay ? "#0d6e1f" : "#d97706",
              "&:hover": { bgcolor: pendingBothWay ? "#0a571a" : "#b45309" },
            }}
          >
            {pendingBothWay ? "Enable two-way sync" : "Turn off two-way sync"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Demo Uninstall confirmation popup ── */}
      <Dialog
        open={uninstallOpen}
        onClose={() => !uninstalling && setUninstallOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", maxWidth: 460 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            fontFamily: FONT_HEADING,
            fontSize: 17,
            fontWeight: 700,
            pb: 1,
          }}
        >
          <PowerSettingsNewOutlinedIcon sx={{ fontSize: 22, color: "#dc2626" }} />
          Run demo uninstall?
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#3f3f46", mb: 1.5 }}>
            This runs the same cleanup the extension performs on uninstall. It
            cannot be undone — you'll need to reconnect afterwards.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              p: "10px 12px",
              borderRadius: "8px",
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
            }}
          >
            <WarningAmberOutlinedIcon
              sx={{ fontSize: 17, mt: "1px", flexShrink: 0, color: "#b91c1c" }}
            />
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: "18px",
                fontSize: 13,
                lineHeight: 1.6,
                color: "#b91c1c",
              }}
            >
              <li>Deletes every Shopify webhook for all connected stores</li>
              <li>Revokes the Shopify access token(s)</li>
              <li>Deletes all sync settings for this org</li>
              <li>Revokes the Zoho CRM connection</li>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
          <Button
            onClick={() => setUninstallOpen(false)}
            variant="outlined"
            size="small"
            disabled={uninstalling}
            sx={{
              textTransform: "none",
              color: "#3f3f46",
              borderColor: "#e4e4e7",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={runDemoUninstall}
            variant="contained"
            size="small"
            disabled={uninstalling}
            startIcon={
              uninstalling ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
            }}
          >
            {uninstalling ? "Running…" : "Run Demo Uninstall"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
